import {
  getTodayAsUTC,
  getNearestDayOfWeek,
  convertDateToIsoDate,
  isTodayVisible,
  getNearestFutureWeekday,
} from '@Components/util/utils'
import { Flex, Button, Text, Separator, Select, IconButton } from '@radix-ui/themes'
import { addDays, isFriday, isMonday, isSaturday, isSunday, parseISO } from 'date-fns'
import { GridDisplayMode } from './types'
import { CalendarIcon } from '@radix-ui/react-icons'
import { useCallback, useRef } from 'react'

export const DateControl = ({
  startDate,
  endDate,
  gridDisplayMode,
  onDateRangeChange,
  onDisplayModeChange,
}: {
  startDate: Date
  endDate: Date
  gridDisplayMode: GridDisplayMode
  onDateRangeChange: (startDate: Date, endDate?: Date) => void
  onDisplayModeChange: (displayMode: GridDisplayMode) => void
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const today = getTodayAsUTC()

  const calculateDaysToAdvance = useCallback(
    (isSubtracting: boolean) => {
      let daysToAdvance = gridDisplayMode === GridDisplayMode.DAY ? 1 : 7
      daysToAdvance = isSubtracting ? daysToAdvance * -1 : daysToAdvance

      const newDate = addDays(startDate, daysToAdvance)
      if (isSaturday(newDate)) {
        daysToAdvance += isSubtracting ? -1 : 2
      } else if (isSunday(newDate)) {
        daysToAdvance += isSubtracting ? -2 : 1
      }

      return daysToAdvance
    },
    [gridDisplayMode, startDate],
  )

  return (
    <Flex gap="3">
      {isTodayVisible(startDate, endDate) ? null : (
        <Flex align="center">
          <Text
            className="return-to-today"
            size="2"
            onClick={() => {
              if (gridDisplayMode === GridDisplayMode.DAY) {
                onDateRangeChange(today)
              } else {
                onDateRangeChange(
                  getNearestDayOfWeek(today, isMonday, false),
                  getNearestDayOfWeek(today, isFriday, true),
                )
              }
            }}>
            {gridDisplayMode === GridDisplayMode.DAY ? 'Return to today' : 'Return to this week'}
          </Text>
        </Flex>
      )}

      <Flex className="date-wrapper">
        <IconButton
          aria-label="Date picker"
          style={{ cursor: 'pointer', alignSelf: 'center', background: 'white' }}
          onClick={() => {
            ref.current?.showPicker()
          }}>
          <CalendarIcon className="calendar-icon" height="20" width="20" />
        </IconButton>
        <input
          type="date"
          className="rt-reset rt-SelectTrigger rt-variant-surface"
          ref={ref}
          value={convertDateToIsoDate(startDate)}
          onChange={(e) => {
            const selectedDay = getNearestFutureWeekday(parseISO(e.target.value))
            if (gridDisplayMode === GridDisplayMode.DAY) {
              onDateRangeChange(selectedDay)
            } else {
              onDateRangeChange(
                getNearestDayOfWeek(selectedDay, isMonday, false),
                getNearestDayOfWeek(selectedDay, isFriday, true),
              )
            }
          }}
        />
      </Flex>

      <Select.Root
        value={gridDisplayMode}
        onValueChange={(value) => {
          if ((value as GridDisplayMode) === GridDisplayMode.DAY) {
            onDateRangeChange(getTodayAsUTC())
          } else {
            // we're switching from the singular day view so start and end should be the same
            onDateRangeChange(
              getNearestDayOfWeek(startDate, isMonday, false),
              getNearestDayOfWeek(startDate, isFriday, true),
            )
          }

          onDisplayModeChange(value as GridDisplayMode)
        }}
        defaultValue={gridDisplayMode}>
        <Select.Trigger className="grid-mode-select" />
        <Select.Content>
          <Select.Item value={GridDisplayMode.DAY}>View By Day</Select.Item>
          <Select.Item value={GridDisplayMode.WEEK}>View By Week</Select.Item>
        </Select.Content>
      </Select.Root>
      <Separator orientation="vertical" size="2" />
      <Button
        variant="outline"
        className="control-button"
        onClick={() => {
          const daysToAdvance = calculateDaysToAdvance(true)
          onDateRangeChange(addDays(startDate, daysToAdvance), addDays(endDate, daysToAdvance))
        }}>
        Previous
      </Button>
      <Button
        className="control-button"
        variant="outline"
        style={{ marginRight: '8px' }}
        onClick={() => {
          const daysToAdvance = calculateDaysToAdvance(false)
          onDateRangeChange(addDays(startDate, daysToAdvance), addDays(endDate, daysToAdvance))
        }}>
        Next
      </Button>
    </Flex>
  )
}
